import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AnalyticsEvent,
  CardTypeEnum,
  Fraction,
  ListItemState,
  ListItemTile,
  Maybe,
} from '@varo/graphql-schema-types';
import { Href, usePathname, useRouter } from 'expo-router';
import { useFetchCardDetails } from 'features/cardManagement/hooks/useFetchCardDetails';
import { PinSetSource } from 'features/cardManagement/screens/PinSet/PinSetSource';
import CircularProgressTracker from 'features/home/HomeScreen/components/CircularProgressTracker/CircularProgressTracker';
import DisclosureBottomSheet from 'features/home/HomeScreen/components/DisclosureBottomSheet';
import OnboardingCheckListBottomSheet from 'features/home/HomeScreen/components/OnboardingCheckListBottomSheet';
import TaskListItem from 'features/home/HomeScreen/components/ProgressCheckList/TaskListItem';
import useGetDDBottomSheetContent from 'features/onboarding/hooks/useGetDDBottomSheetContent';
import { useEffect, useRef, useState } from 'react';
import { FlatList, Linking, StyleSheet, View } from 'react-native';
import { parseOptionalGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { ToggleTile } from 'services/graphql/mutations/ToggleTile';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import BodyXSBoldText from 'shared/components/vxl/typography/primary/body/BodyXSBoldText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import {
  ANDROID_APP_PLAY_STORE_URL,
  IOS_APP_STORE_URL,
} from 'shared/constants/appStore';
import {
  isAndroid,
  isAndroidBrowser,
  isDesktopBrowser,
  isIOS,
  isIOSBrowser,
  isWeb,
} from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { useMutation } from 'urql';

interface ProgressCheckListProps {
  welcomeMessage: string;
  title: string;
  listItems: ListItemTile[];
  progress: Maybe<Fraction>;
  displayAnalytics?: Maybe<AnalyticsEvent>;
}

const ProgressCheckList = ({
  welcomeMessage,
  title,
  listItems,
  progress,
  displayAnalytics,
}: ProgressCheckListProps) => {
  const router = useRouter();
  const isCompleteChecklist = progress?.numerator === progress?.denominator;
  const currentURL = usePathname();
  const [isDDBottomSheetPresented, setIsDDBottomSheetPresented] =
    useState(false);
  const { bottomSheetData } = useGetDDBottomSheetContent({
    skip: !isCompleteChecklist,
  });
  const [isDisclosurePresented, setIsDisclosurePresented] = useState(false);
  const { sourceData } = useFetchCardDetails();
  const ddBottomSheetRef = useRef<BottomSheetModal | null>(null);
  const disclosureBottomSheetRef = useRef<BottomSheetModal | null>(null);
  const hasAutoCompletedRef = useRef(false);
  const { segmentClient } = useAnalyticsConfiguration();

  const [, toggleTile] = useMutation(ToggleTile);

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_WELCOME,
  ]);

  useEffect(() => {
    const analytics = parseOptionalGQLAnalyticsEvent(displayAnalytics);
    if (analytics) {
      void segmentClient.logEvent(analytics.name, analytics.properties);
    }
  }, [displayAnalytics, segmentClient]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  useEffect(() => {
    if (isCompleteChecklist && bottomSheetData && currentURL === '/') {
      setIsDDBottomSheetPresented(true);
      ddBottomSheetRef.current?.present();
    }
  }, [isCompleteChecklist, bottomSheetData, currentURL]);

  useEffect(() => {
    const autoCompleteGetVaroAppTile = async () => {
      if (hasAutoCompletedRef.current || (!isIOS && !isAndroid) || isWeb) {
        return;
      }

      const getVaroAppTile = listItems.find(
        (item) => item?.tileType === 'GET_THE_VARO_APP_TILE',
      );

      if (!getVaroAppTile) {
        return;
      }

      if (
        getVaroAppTile.toggleToken &&
        getVaroAppTile.state !== ListItemState.Completed
      ) {
        hasAutoCompletedRef.current = true;

        try {
          const result = await toggleTile({
            toggleToken: getVaroAppTile.toggleToken,
          });

          if (!result.error && result.data) {
            refreshTiles({ requestPolicy: 'network-only' });
          }
        } catch (_error) {}
      } else if (getVaroAppTile.state === ListItemState.Completed) {
        hasAutoCompletedRef.current = true;
      }
    };

    if (listItems.length > 0) {
      autoCompleteGetVaroAppTile();
    }
  }, [listItems, refreshTiles, toggleTile]);

  const debitCardId: string | null =
    sourceData?.cards?.find((card) => card?.type === CardTypeEnum.DebitCard)
      ?.id ?? null;

  const varoAppClick = usePressableAnalytics({
    analyticsId: 'onboardingChecklistClicked',
    customAttributes: {
      onboarding_checklist: 'varo_app',
    },
  });
  const cardSetupClick = usePressableAnalytics({
    analyticsId: 'onboardingChecklistClicked',
    customAttributes: {
      onboarding_checklist: 'card_setup',
    },
  });
  const fundAccountClick = usePressableAnalytics({
    analyticsId: 'onboardingChecklistClicked',
    customAttributes: {
      onboarding_checklist: 'fund_account',
    },
  });

  const spendBenefitsClick = usePressableAnalytics({
    analyticsId: 'onboardingChecklistClicked',
    customAttributes: {
      onboarding_checklist: 'first_purchase',
    },
  });

  const handleTileClick = async (tile: ListItemTile) => {
    switch (tile?.tileType) {
      case 'GET_THE_VARO_APP_TILE':
        varoAppClick();
        if (!isWeb && tile?.toggleToken) {
          const result = await toggleTile({ toggleToken: tile.toggleToken });
          if (!result.error) {
            refreshTiles({ requestPolicy: 'network-only' });
          }
        } else if (isIOSBrowser) {
          await Linking.openURL(IOS_APP_STORE_URL);
        } else if (isAndroidBrowser) {
          await Linking.openURL(ANDROID_APP_PLAY_STORE_URL);
        } else if (isDesktopBrowser) {
          router.push('/get-varo-app');
        }
        break;

      case 'SET_UP_CARD_TILE':
        cardSetupClick();
        router.push({
          pathname: `/myvaro/card/new-pin/${debitCardId}`,
          params: { source: PinSetSource.ProgressChecklist },
        } as Href);
        break;

      case 'MAKE_YOUR_FIRST_PURCHASE_TILE':
        spendBenefitsClick();
        router.push('/spend-benefits');
        break;

      case 'FUND_ACCOUNT_MOVE_MONEY_OR_SET_UP_DD_TILE':
        fundAccountClick();
        router.push('/progress-tracker-funding' as Href);
        break;
    }
  };

  return (
    <View style={styles.container}>
      <DisclosureBottomSheet
        bottomSheetRef={disclosureBottomSheetRef}
        isDisclosurePresented={isDisclosurePresented}
        disclosureData={bottomSheetData?.disclosure}
      />
      {isCompleteChecklist && (
        <OnboardingCheckListBottomSheet
          bottomSheetData={bottomSheetData}
          isDDBottomSheetPresented={isDDBottomSheetPresented}
          isDdSetUpDone={
            bottomSheetData?.type &&
            bottomSheetData.type === 'EARNING_CASH_BACK'
          }
          bottomSheetRef={ddBottomSheetRef}
          onDisclosurePress={() => {
            setIsDisclosurePresented(true);
            disclosureBottomSheetRef?.current?.present();
          }}
        />
      )}
      <View style={styles.headerContainer}>
        <View style={styles.textContainer}>
          <BodyXSBoldText style={styles.welcomeMessage}>
            {welcomeMessage}
          </BodyXSBoldText>
          <TitleMdText>{title}</TitleMdText>
        </View>
        {progress && (
          <CircularProgressTracker
            completedSteps={progress.numerator}
            totalSteps={progress.denominator}
          />
        )}
      </View>
      <View style={styles.flatListContainer}>
        <FlatList
          data={listItems}
          scrollEnabled={false}
          nestedScrollEnabled
          keyExtractor={(item) => item?.id}
          renderItem={({ item }) => {
            let iconColor = theme.color.text.secondary;
            if (item?.state === ListItemState.Disabled) {
              iconColor = theme.color.interactive.disabled.text;
            } else if (item?.state === ListItemState.Completed) {
              iconColor = theme.color.interactive.primary;
            }
            return (
              <TaskListItem
                primaryCopy={item?.title}
                secondaryCopy={item.caption}
                isDisabled={item?.state === ListItemState.Disabled}
                isStepCompleted={item?.state === ListItemState.Completed}
                onPress={() => handleTileClick(item)}
                leadingAccessory={{
                  id: '1',
                  name: VXLComponentId.ICON,
                  props: {
                    iconKey:
                      item?.state === ListItemState.Completed
                        ? 'LIST_CHECK_MARK'
                        : 'CIRCLE',
                    iconAccessibilityLabel:
                      item?.state === ListItemState.Completed
                        ? 'step_completed'
                        : 'step_not_completed',
                    iconColor,
                  },
                }}
              />
            );
          }}
          ItemSeparatorComponent={() => (
            <View style={styles.hairLineDivider}>
              <HairLineDivider />
            </View>
          )}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing.m,
  },
  flatListContainer: {
    backgroundColor: theme.color.interactive.tertiary,
    borderRadius: theme.spacing.xs,
  },
  hairLineDivider: {
    backgroundColor: theme.color.border.primary,
    marginHorizontal: theme.spacing.m,
  },
  headerContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  textContainer: {
    flex: 1,
    gap: theme.spacing.s,
  },
  welcomeMessage: {
    color: theme.color.text.secondary,
  },
});

export default ProgressCheckList;
